import api from "./api";

/**
 * Coding-profile client. Students link competitive-programming handles and
 * read cached public stats (Codeforces/LeetCode/HackerRank/CodeChef live;
 * HackerEarth/SPOJ store handles and light up when the backend grows a
 * client). All endpoints are self-scoped on the backend and 404 while
 * CODING_PROFILES_ENABLED is off — the UI degrades gracefully (callers treat
 * a 404 as "feature not enabled").
 */

// Supported platforms + display labels — single FE source (mirrors backend
// config/codingProfiles.js CODING_PLATFORMS). `live` = the backend has a
// stats-fetch client today (services/coding CLIENTS).
export const CODING_PLATFORMS = [
  { key: "codeforces", label: "Codeforces", live: true },
  { key: "leetcode", label: "LeetCode", live: true },
  { key: "codechef", label: "CodeChef", live: true },
  { key: "hackerrank", label: "HackerRank", live: true },
  { key: "hackerearth", label: "HackerEarth", live: false },
  { key: "spoj", label: "SPOJ", live: false },
];

// Internal 7th platform — practice stats derived from the student's own
// exam coding answers (no handle to link).
export const INTERNAL_PLATFORM = { key: "internal", label: "Exam practice" };

// Every platform that can appear in the dashboard charts.
export const ALL_CHART_PLATFORMS = [...CODING_PLATFORMS, INTERNAL_PLATFORM];

// Period windows for GET /me?period= — single FE source (mirrors backend
// config/codingProfiles.js CODING_STAT_PERIODS + the implicit "overall").
export const CODING_STAT_PERIODS = [
  { key: "overall", label: "Overall" },
  { key: "7d", label: "Last 7 Days" },
  { key: "30d", label: "Last 30 Days" },
  { key: "6m", label: "Last 6 Months" },
];

// Handle length cap — single FE source (mirrors backend
// config/codingProfiles.js CODING_HANDLE_MAX_LEN).
export const CODING_HANDLE_MAX_LEN = 64;

// Ship-dark probe — the backend 404s the whole /coding-profiles surface while
// CODING_PROFILES_ENABLED is off (same signal CodingProfileV2 uses for its
// "not enabled yet" state). Nav items consume this to stay hidden until the
// flag is on. Memoized at module level so the app probes at most once per
// load; non-404 failures (transient/network) clear the cache so a later
// mount can re-probe, but still resolve false (default hidden).
let codingProfilesEnabledProbe = null;
export const isCodingProfilesEnabled = async () => {
  if (!codingProfilesEnabledProbe) {
    codingProfilesEnabledProbe = api
      .get("/coding-profiles/me")
      .then(() => true)
      .catch((e) => {
        if (e?.response?.status === 404) return false; // flag off
        codingProfilesEnabledProbe = null;
        return false;
      });
  }
  return codingProfilesEnabledProbe;
};

// Handles + cached stats snapshot. `period` scopes stats.periodTotals to a
// window ("7d"/"30d"/"6m") — delta vs the closest old-enough history
// snapshot, null when there is not enough history yet.
export const getMyCodingProfile = async (period = "overall") => {
  const res = await api.get("/coding-profiles/me", {
    params: period && period !== "overall" ? { period } : {},
  });
  return res.data; // { success, enabled, handles, stats, period, verified, prefs }
};

// Save { codeforces, leetcode, ... } handle map ("" clears a link). Each
// saved handle is existence-checked → verified { <platform>: true|false|null }.
export const saveMyHandles = async (handles) => {
  const res = await api.put("/coding-profiles/me/handles", handles);
  return res.data; // { success, handles, verified }
};

// Dashboard chart visibility — hiddenPlatforms may include "internal".
export const saveMyCodingPrefs = async (hiddenPlatforms) => {
  const res = await api.put("/coding-profiles/me/prefs", { hiddenPlatforms });
  return res.data; // { success, prefs: { hiddenPlatforms } }
};

// Immediate stat sync for the caller's linked handles (429 when too soon).
export const refreshMyCodingStats = async () => {
  const res = await api.post("/coding-profiles/me/refresh");
  return res.data; // { success, stats }
};
